/**
 * Pharmacy Dispensing queries — PRD.194.
 *
 * The Pharmacy queue is DERIVED from paid invoice items that reference a
 * Medicine inventory item and have not yet been fully dispensed. The
 * stock-out event is `confirmDispense`; this module exposes only reads.
 * The actual write path lives in the Pharmacy page and creates rows in:
 *   - pharmacy_dispenses
 *   - pharmacy_dispense_lines
 *   - stock_movements (source_type='pharmacy_dispense')
 *
 * Eligibility rule:
 *   (invoices.status = 'paid' OR invoice_items.payment_status = 'paid')
 *   -- Per-item gate: a line is dispensable once IT is fully paid, even if the
 *   -- rest of the invoice is still outstanding. invoice_items.payment_status is
 *   -- recomputed client-side from payment allocations (see the web-admin
 *   -- invoice-item-settlement service). The invoice-level `status = 'paid'`
 *   -- arm is kept so invoices settled before this column existed still qualify.
 *   AND invoice_items.inventory_item_id IS NOT NULL
 *   AND (
 *     inventory_items.item_type IN ('medicine', 'vaccination')
 *     OR (
 *       inventory_items.item_type = 'product'
 *       AND inventory_items.dispense_via_pharmacy = 1
 *     )
 *   )
 *   AND COALESCE(SUM(confirmed pharmacy_dispense_lines.quantity_dispensed), 0)
 *       < invoice_items.qty
 *
 * PRD.234 — Products with `dispense_via_pharmacy = 1` follow the same
 * workflow as medicines. Their `item_type` stays 'product' so non-
 * pharmacy product workflows (retail Product Release) keep their
 * domain identity; only the Pharmacy queue/dispense pieces widen.
 *
 * PRD.266 — Vaccinations are stock-tracked administered items that fulfill
 * through the same Pharmacy dispense flow (queue → batch pick → confirm →
 * stock_movements). Injections are non-stock and never enter this queue.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface PharmacyDispenseRow {
  id: string;
  org_id: string | null;
  dispense_number: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  invoice_id: string | null;
  prescription_id: string | null;
  status: string | null; // 'draft' | 'confirmed' | 'cancelled'
  dispensed_at: string | null;
  dispensed_by: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PharmacyDispenseLineRow {
  id: string;
  org_id: string | null;
  pharmacy_dispense_id: string | null;
  invoice_item_id: string | null;
  prescription_line_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  unit_snapshot: string | null;
  instructions_snapshot: string | null;
  quantity_to_dispense: number | null;
  quantity_dispensed: number | null;
  status: string | null; // 'pending' | 'confirmed' | 'cancelled'
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * One row per pending dispense-eligible invoice item line. Already
 * carries patient / invoice / item context for queue rendering.
 *
 * `quantity_remaining` = invoice line qty − total confirmed dispensed
 * for that invoice line. Always > 0 because rows where remaining = 0
 * are filtered out at the SQL level.
 */
export interface PharmacyQueueRow {
  invoice_item_id: string;
  invoice_id: string | null;
  invoice_no: string | null;
  invoice_status: string | null;
  invoice_date: string | null;
  patient_id: string | null;
  patient_name: string | null;
  encounter_id: string | null;

  inventory_item_id: string | null;
  item_name: string | null;
  sku: string | null;
  inventory_unit: string | null;

  // From invoice line snapshots (PRD.190).
  description_snapshot: string | null;
  unit_snapshot: string | null;

  qty_ordered: number | null;
  qty_dispensed: number | null;
  quantity_remaining: number | null;
}

// ─── Queries ───

/**
 * Pharmacy queue: pending Medicine lines on paid invoices, grouped by
 * invoice. Excludes lines that have already been fully dispensed.
 *
 * The aggregate sub-select sums only `status='confirmed'` dispense lines
 * so a future cancelled-dispense flow won't double-count.
 */
export function qPharmacyQueue(): QuerySpec<[]> {
  return {
    key: ['pharmacy-queue'] as const,
    sql: `
      SELECT
        ii.id              AS invoice_item_id,
        ii.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        i.status           AS invoice_status,
        i.date_iso         AS invoice_date,
        i.patient_id       AS patient_id,
        i.patient_name     AS patient_name,
        i.encounter_id     AS encounter_id,
        ii.inventory_item_id AS inventory_item_id,
        inv.item_name      AS item_name,
        inv.sku            AS sku,
        inv.inventory_unit AS inventory_unit,
        ii.description_snapshot AS description_snapshot,
        ii.unit_snapshot   AS unit_snapshot,
        ii.qty             AS qty_ordered,
        COALESCE(d.qty_dispensed, 0) AS qty_dispensed,
        COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) AS quantity_remaining
      FROM invoice_items ii
      JOIN invoices i  ON i.id  = ii.invoice_id
      JOIN inventory_items inv ON inv.id = ii.inventory_item_id
      LEFT JOIN (
        SELECT
          invoice_item_id,
          SUM(COALESCE(quantity_dispensed, 0)) AS qty_dispensed
        FROM pharmacy_dispense_lines
        WHERE status = 'confirmed'
        GROUP BY invoice_item_id
      ) d ON d.invoice_item_id = ii.id
      WHERE ii.inventory_item_id IS NOT NULL
        AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
        AND (i.status = 'paid' OR ii.payment_status = 'paid')
        AND COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) > 0
      ORDER BY i.date_iso DESC, i.invoice_no ASC, ii.sort_order ASC, ii.created_at ASC
    `,
    params: [],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * PRD.200 — Aggregate counts that drive the homepage Pharmacy widget.
 *
 * Same eligibility rule as `qPharmacyQueue` so the homepage number
 * matches the global queue's row count exactly:
 *   invoice.status='paid' AND inventory_item_id IS NOT NULL
 *   AND inventory_items.item_type='medicine'
 *   AND remaining > 0
 *
 * `oldest_ready_date` is the oldest invoice date among ready/partial
 * lines. We use `invoice.date_iso` since it is the only "paid era"
 * timestamp available in v1; documenting per PRD.200 §8.4.
 */
export interface PharmacyQueueSummaryRow {
  ready_count: number | null;
  partial_count: number | null;
  patient_count: number | null;
  oldest_ready_date: string | null;
}

export function qPharmacyQueueSummary(): QuerySpec<[]> {
  return {
    key: ['pharmacy-queue-summary'] as const,
    sql: `
      WITH lines AS (
        SELECT
          i.patient_id  AS patient_id,
          i.date_iso    AS invoice_date,
          COALESCE(ii.qty, 0) AS qty_ordered,
          COALESCE((
            SELECT SUM(COALESCE(quantity_dispensed, 0))
            FROM pharmacy_dispense_lines pdl
            WHERE pdl.invoice_item_id = ii.id
              AND pdl.status = 'confirmed'
          ), 0) AS qty_dispensed
        FROM invoice_items ii
        JOIN invoices i ON i.id = ii.invoice_id
        JOIN inventory_items inv ON inv.id = ii.inventory_item_id
        WHERE ii.inventory_item_id IS NOT NULL
          AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
          AND (i.status = 'paid' OR ii.payment_status = 'paid')
      ),
      ready AS (
        SELECT * FROM lines WHERE qty_ordered - qty_dispensed > 0
      )
      SELECT
        COUNT(*) AS ready_count,
        SUM(CASE WHEN qty_dispensed > 0 THEN 1 ELSE 0 END) AS partial_count,
        COUNT(DISTINCT patient_id) AS patient_count,
        MIN(invoice_date) AS oldest_ready_date
      FROM ready
    `,
    params: [],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * PRD.199 — Patient-scoped Pharmacy queue. Same eligibility rules as
 * `qPharmacyQueue` but restricted to a single patient. Used by the
 * Patient Pharmacy panel so staff can dispense the current patient's
 * Medicine invoice lines without leaving patient context.
 */
export function qPatientPharmacyQueue(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-pharmacy-queue', patientId] as const,
    sql: `
      SELECT
        ii.id              AS invoice_item_id,
        ii.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        i.status           AS invoice_status,
        i.date_iso         AS invoice_date,
        i.patient_id       AS patient_id,
        i.patient_name     AS patient_name,
        i.encounter_id     AS encounter_id,
        ii.inventory_item_id AS inventory_item_id,
        inv.item_name      AS item_name,
        inv.sku            AS sku,
        inv.inventory_unit AS inventory_unit,
        ii.description_snapshot AS description_snapshot,
        ii.unit_snapshot   AS unit_snapshot,
        ii.qty             AS qty_ordered,
        COALESCE(d.qty_dispensed, 0) AS qty_dispensed,
        COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) AS quantity_remaining
      FROM invoice_items ii
      JOIN invoices i  ON i.id  = ii.invoice_id
      JOIN inventory_items inv ON inv.id = ii.inventory_item_id
      LEFT JOIN (
        SELECT
          invoice_item_id,
          SUM(COALESCE(quantity_dispensed, 0)) AS qty_dispensed
        FROM pharmacy_dispense_lines
        WHERE status = 'confirmed'
        GROUP BY invoice_item_id
      ) d ON d.invoice_item_id = ii.id
      WHERE i.patient_id = ?
        AND ii.inventory_item_id IS NOT NULL
        AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
        AND (i.status = 'paid' OR ii.payment_status = 'paid')
        AND COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) > 0
      ORDER BY i.date_iso DESC, i.invoice_no ASC, ii.sort_order ASC, ii.created_at ASC
    `,
    params: [patientId],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * PRD.201 — Encounter-scoped Pharmacy queue. Same eligibility rule as
 * `qPharmacyQueue`/`qPatientPharmacyQueue` but filtered to a single
 * encounter. Used by the Encounter Pharmacy tab.
 */
export function qEncounterPharmacyQueue(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['encounter-pharmacy-queue', patientId, encounterId] as const,
    sql: `
      SELECT
        ii.id              AS invoice_item_id,
        ii.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        i.status           AS invoice_status,
        i.date_iso         AS invoice_date,
        i.patient_id       AS patient_id,
        i.patient_name     AS patient_name,
        i.encounter_id     AS encounter_id,
        ii.inventory_item_id AS inventory_item_id,
        inv.item_name      AS item_name,
        inv.sku            AS sku,
        inv.inventory_unit AS inventory_unit,
        ii.description_snapshot AS description_snapshot,
        ii.unit_snapshot   AS unit_snapshot,
        ii.qty             AS qty_ordered,
        COALESCE(d.qty_dispensed, 0) AS qty_dispensed,
        COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) AS quantity_remaining
      FROM invoice_items ii
      JOIN invoices i  ON i.id  = ii.invoice_id
      JOIN inventory_items inv ON inv.id = ii.inventory_item_id
      LEFT JOIN (
        SELECT
          invoice_item_id,
          SUM(COALESCE(quantity_dispensed, 0)) AS qty_dispensed
        FROM pharmacy_dispense_lines
        WHERE status = 'confirmed'
        GROUP BY invoice_item_id
      ) d ON d.invoice_item_id = ii.id
      WHERE i.patient_id = ?
        AND i.encounter_id = ?
        AND ii.inventory_item_id IS NOT NULL
        AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
        AND (i.status = 'paid' OR ii.payment_status = 'paid')
        AND COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) > 0
      ORDER BY i.date_iso DESC, i.invoice_no ASC, ii.sort_order ASC, ii.created_at ASC
    `,
    params: [patientId, encounterId],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * PRD.201 — Encounter-scoped dispense history. Includes dispenses whose
 * pharmacy_dispense.encounter_id matches OR whose linked invoice's
 * encounter_id matches — covers older rows that may have been written
 * before the dispense header captured encounter_id directly.
 */
export function qEncounterPharmacyDispenseHistory(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string, string]> {
  return {
    key: ['encounter-pharmacy-dispense-history', patientId, encounterId] as const,
    sql: `
      SELECT
        pd.id              AS pharmacy_dispense_id,
        pdl.id             AS pharmacy_dispense_line_id,
        pd.dispense_number AS dispense_number,
        pd.dispensed_at    AS dispensed_at,
        pd.dispensed_by    AS dispensed_by,
        pd.patient_id      AS patient_id,
        COALESCE(NULLIF(TRIM(i.patient_name), ''), p.full_name) AS patient_name,
        COALESCE(pd.encounter_id, i.encounter_id) AS encounter_id,
        pd.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        pdl.invoice_item_id        AS invoice_item_id,
        pdl.inventory_item_id      AS inventory_item_id,
        pdl.item_name_snapshot     AS item_name_snapshot,
        pdl.sku_snapshot           AS sku_snapshot,
        pdl.batch_number_snapshot  AS batch_number_snapshot,
        pdl.expiry_date_snapshot   AS expiry_date_snapshot,
        pdl.unit_snapshot          AS unit_snapshot,
        pdl.instructions_snapshot  AS instructions_snapshot,
        pdl.quantity_dispensed     AS quantity_dispensed,
        pdl.remarks                AS remarks
      FROM pharmacy_dispense_lines pdl
      JOIN pharmacy_dispenses pd ON pd.id = pdl.pharmacy_dispense_id
      LEFT JOIN invoices i ON i.id = pd.invoice_id
      LEFT JOIN patients p ON p.id = pd.patient_id
      WHERE pd.patient_id = ?
        AND pdl.status = 'confirmed'
        AND (pd.encounter_id = ? OR i.encounter_id = ?)
      ORDER BY pd.dispensed_at DESC, pdl.created_at ASC
    `,
    params: [patientId, encounterId, encounterId],
    watch: ['pharmacy_dispenses', 'pharmacy_dispense_lines', 'invoices'],
  };
}

/**
 * PRD.201 — Summary counts for the encounter Pharmacy tab. Same shape as
 * `qPatientPharmacySummary` so the two can share rendering helpers.
 */
export function qEncounterPharmacySummary(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['encounter-pharmacy-summary', patientId, encounterId] as const,
    sql: `
      WITH lines AS (
        SELECT
          ii.id  AS invoice_item_id,
          COALESCE(ii.qty, 0) AS qty_ordered,
          COALESCE((
            SELECT SUM(COALESCE(quantity_dispensed, 0))
            FROM pharmacy_dispense_lines pdl
            WHERE pdl.invoice_item_id = ii.id
              AND pdl.status = 'confirmed'
          ), 0) AS qty_dispensed
        FROM invoice_items ii
        JOIN invoices i ON i.id = ii.invoice_id
        JOIN inventory_items inv ON inv.id = ii.inventory_item_id
        WHERE i.patient_id = ?
          AND i.encounter_id = ?
          AND ii.inventory_item_id IS NOT NULL
          AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
          AND (i.status = 'paid' OR ii.payment_status = 'paid')
      )
      SELECT
        SUM(CASE WHEN qty_dispensed = 0 AND qty_ordered > 0 THEN 1 ELSE 0 END) AS ready_count,
        SUM(CASE WHEN qty_dispensed > 0 AND qty_dispensed < qty_ordered THEN 1 ELSE 0 END) AS partial_count,
        SUM(CASE WHEN qty_dispensed >= qty_ordered AND qty_ordered > 0 THEN 1 ELSE 0 END) AS dispensed_count
      FROM lines
    `,
    params: [patientId, encounterId],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * One row per confirmed pharmacy dispense line for a patient. Used by the
 * Patient Pharmacy history section to show what has already been dispensed.
 *
 * `patient_name` and `encounter_id` are surfaced for PRD.202 — Pharmacy
 * Print Label needs the patient name and Print Prescription needs the
 * encounter id to navigate back into the encounter's Prescriptions tab.
 */
export interface PatientPharmacyDispenseHistoryRow {
  pharmacy_dispense_id: string | null;
  pharmacy_dispense_line_id: string;
  dispense_number: string | null;
  dispensed_at: string | null;
  dispensed_by: string | null;
  patient_id: string | null;
  patient_name: string | null;
  encounter_id: string | null;
  invoice_id: string | null;
  invoice_no: string | null;
  invoice_item_id: string | null;
  inventory_item_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  unit_snapshot: string | null;
  instructions_snapshot: string | null;
  quantity_dispensed: number | null;
  remarks: string | null;
}

export function qPatientPharmacyDispenseHistory(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-pharmacy-dispense-history', patientId] as const,
    sql: `
      SELECT
        pd.id              AS pharmacy_dispense_id,
        pdl.id             AS pharmacy_dispense_line_id,
        pd.dispense_number AS dispense_number,
        pd.dispensed_at    AS dispensed_at,
        pd.dispensed_by    AS dispensed_by,
        pd.patient_id      AS patient_id,
        COALESCE(NULLIF(TRIM(i.patient_name), ''), p.full_name) AS patient_name,
        COALESCE(pd.encounter_id, i.encounter_id) AS encounter_id,
        pd.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        pdl.invoice_item_id        AS invoice_item_id,
        pdl.inventory_item_id      AS inventory_item_id,
        pdl.item_name_snapshot     AS item_name_snapshot,
        pdl.sku_snapshot           AS sku_snapshot,
        pdl.batch_number_snapshot  AS batch_number_snapshot,
        pdl.expiry_date_snapshot   AS expiry_date_snapshot,
        pdl.unit_snapshot          AS unit_snapshot,
        pdl.instructions_snapshot  AS instructions_snapshot,
        pdl.quantity_dispensed     AS quantity_dispensed,
        pdl.remarks                AS remarks
      FROM pharmacy_dispense_lines pdl
      JOIN pharmacy_dispenses pd ON pd.id = pdl.pharmacy_dispense_id
      LEFT JOIN invoices i ON i.id = pd.invoice_id
      LEFT JOIN patients p ON p.id = pd.patient_id
      WHERE pd.patient_id = ?
        AND pdl.status = 'confirmed'
      ORDER BY pd.dispensed_at DESC, pdl.created_at ASC
    `,
    params: [patientId],
    watch: ['pharmacy_dispenses', 'pharmacy_dispense_lines', 'invoices'],
  };
}

/**
 * Aggregate counts for the Patient Pharmacy summary cards. Pending counts
 * Medicine invoice lines on paid invoices that still have remaining qty;
 * partial covers lines with at least one confirmed dispense but not yet
 * fully dispensed; fully counts lines whose total dispensed >= ordered qty.
 */
export interface PatientPharmacySummaryRow {
  ready_count: number;
  partial_count: number;
  dispensed_count: number;
}

export function qPatientPharmacySummary(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-pharmacy-summary', patientId] as const,
    sql: `
      WITH lines AS (
        SELECT
          ii.id  AS invoice_item_id,
          COALESCE(ii.qty, 0) AS qty_ordered,
          COALESCE((
            SELECT SUM(COALESCE(quantity_dispensed, 0))
            FROM pharmacy_dispense_lines pdl
            WHERE pdl.invoice_item_id = ii.id
              AND pdl.status = 'confirmed'
          ), 0) AS qty_dispensed
        FROM invoice_items ii
        JOIN invoices i ON i.id = ii.invoice_id
        JOIN inventory_items inv ON inv.id = ii.inventory_item_id
        WHERE i.patient_id = ?
          AND ii.inventory_item_id IS NOT NULL
          AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
          AND (i.status = 'paid' OR ii.payment_status = 'paid')
      )
      SELECT
        SUM(CASE WHEN qty_dispensed = 0 AND qty_ordered > 0 THEN 1 ELSE 0 END) AS ready_count,
        SUM(CASE WHEN qty_dispensed > 0 AND qty_dispensed < qty_ordered THEN 1 ELSE 0 END) AS partial_count,
        SUM(CASE WHEN qty_dispensed >= qty_ordered AND qty_ordered > 0 THEN 1 ELSE 0 END) AS dispensed_count
      FROM lines
    `,
    params: [patientId],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

// ─── PRD.236 — Queue Dispense readiness by visit date ───────────────────────
//
// One row per patient with at least one dispensable line on a paid invoice
// for a visit on the given date. Drives the Queue Dispense step's visibility
// without per-row queries. Eligibility mirrors `qPatientPharmacySummary`
// (Medicine lines on paid invoices, or Product lines where the inventory item
// has `dispense_via_pharmacy = 1`).

export interface ReadyToDispenseByPatientRow {
  patient_id: string;
  ready_count: number;
}

export function qReadyToDispenseByDate(date: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-ready-by-date', date] as const,
    sql: `
      WITH lines AS (
        SELECT
          v.patient_id,
          ii.id AS invoice_item_id,
          COALESCE(ii.qty, 0) AS qty_ordered,
          COALESCE((
            SELECT SUM(COALESCE(quantity_dispensed, 0))
            FROM pharmacy_dispense_lines pdl
            WHERE pdl.invoice_item_id = ii.id
              AND pdl.status = 'confirmed'
          ), 0) AS qty_dispensed
        FROM patient_visits v
        JOIN invoices i ON i.visit_id = v.id
        JOIN invoice_items ii ON ii.invoice_id = i.id
        JOIN inventory_items inv ON inv.id = ii.inventory_item_id
        WHERE v.visit_date = ?
          AND ii.inventory_item_id IS NOT NULL
          AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
          AND (i.status = 'paid' OR ii.payment_status = 'paid')
      )
      SELECT
        patient_id,
        SUM(CASE WHEN qty_dispensed < qty_ordered AND qty_ordered > 0 THEN 1 ELSE 0 END) AS ready_count
      FROM lines
      GROUP BY patient_id
      HAVING ready_count > 0
    `,
    params: [date],
    watch: [
      'patient_visits',
      'invoices',
      'invoice_items',
      'inventory_items',
      'pharmacy_dispense_lines',
    ],
  };
}

/**
 * Same queue scoped to a single invoice. Useful when the user opens an
 * invoice card to confirm one or more medicine lines.
 */
export function qPharmacyQueueLines(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-queue-by-invoice', invoiceId] as const,
    sql: `
      SELECT
        ii.id              AS invoice_item_id,
        ii.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        i.status           AS invoice_status,
        i.date_iso         AS invoice_date,
        i.patient_id       AS patient_id,
        i.patient_name     AS patient_name,
        i.encounter_id     AS encounter_id,
        ii.inventory_item_id AS inventory_item_id,
        inv.item_name      AS item_name,
        inv.sku            AS sku,
        inv.inventory_unit AS inventory_unit,
        ii.description_snapshot AS description_snapshot,
        ii.unit_snapshot   AS unit_snapshot,
        ii.qty             AS qty_ordered,
        COALESCE(d.qty_dispensed, 0) AS qty_dispensed,
        COALESCE(ii.qty, 0) - COALESCE(d.qty_dispensed, 0) AS quantity_remaining
      FROM invoice_items ii
      JOIN invoices i  ON i.id  = ii.invoice_id
      JOIN inventory_items inv ON inv.id = ii.inventory_item_id
      LEFT JOIN (
        SELECT
          invoice_item_id,
          SUM(COALESCE(quantity_dispensed, 0)) AS qty_dispensed
        FROM pharmacy_dispense_lines
        WHERE status = 'confirmed'
        GROUP BY invoice_item_id
      ) d ON d.invoice_item_id = ii.id
      WHERE ii.invoice_id = ?
        AND ii.inventory_item_id IS NOT NULL
        AND (inv.item_type IN ('medicine', 'vaccination') OR (inv.item_type = 'product' AND inv.dispense_via_pharmacy = 1))
      ORDER BY ii.sort_order ASC, ii.created_at ASC
    `,
    params: [invoiceId],
    watch: ['invoice_items', 'invoices', 'inventory_items', 'pharmacy_dispense_lines'],
  };
}

/**
 * All confirmed dispense lines for a given invoice item. Use to drive
 * duplicate-dispense detection or to show dispense history beneath a
 * queue row.
 */
/**
 * PRD.196 — Derived dispense status for every invoice item on an invoice.
 *
 * Returns one row per invoice line, with `total_dispensed` aggregated from
 * `pharmacy_dispense_lines` rows whose `status = 'confirmed'`. Lines with
 * no confirmed dispenses appear with `total_dispensed = 0` so the consumer
 * doesn't need a separate "no dispense yet" branch.
 *
 * Status mapping (computed in the consumer, not in SQL, to keep this query
 * stable as future statuses are added):
 *   - inventory_item_id IS NULL              → 'not_pharmacy_item'
 *   - total_dispensed === 0                  → 'pending_dispense' or
 *                                              'awaiting_payment' depending
 *                                              on invoice.status
 *   - 0 < total_dispensed < qty              → 'partially_dispensed'
 *   - total_dispensed >= qty                 → 'dispensed'
 */
export interface InvoiceItemDispenseStatusRow {
  invoice_item_id: string;
  invoice_id: string | null;
  qty: number | null;
  inventory_item_id: string | null;
  total_dispensed: number;
}

export function qInvoiceItemDispenseStatuses(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice-item-dispense-statuses', invoiceId] as const,
    sql: `
      SELECT
        ii.id                AS invoice_item_id,
        ii.invoice_id        AS invoice_id,
        ii.qty               AS qty,
        ii.inventory_item_id AS inventory_item_id,
        COALESCE((
          SELECT SUM(COALESCE(pdl.quantity_dispensed, 0))
          FROM pharmacy_dispense_lines pdl
          WHERE pdl.invoice_item_id = ii.id
            AND pdl.status = 'confirmed'
        ), 0) AS total_dispensed
      FROM invoice_items ii
      WHERE ii.invoice_id = ?
      ORDER BY ii.sort_order ASC, ii.created_at ASC
    `,
    params: [invoiceId],
    watch: ['invoice_items', 'pharmacy_dispense_lines'],
  };
}

export function qPharmacyDispensesByInvoiceItem(invoiceItemId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-dispenses-by-invoice-item', invoiceItemId] as const,
    sql: `
      SELECT * FROM pharmacy_dispense_lines
      WHERE invoice_item_id = ?
      ORDER BY created_at ASC
    `,
    params: [invoiceItemId],
    watch: ['pharmacy_dispense_lines'],
  };
}

export function qPharmacyDispenseLinesByDispense(dispenseId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-dispense-lines', dispenseId] as const,
    sql: `
      SELECT * FROM pharmacy_dispense_lines
      WHERE pharmacy_dispense_id = ?
      ORDER BY created_at ASC
    `,
    params: [dispenseId],
    watch: ['pharmacy_dispense_lines'],
  };
}

/**
 * Existing batches for an inventory item, with computed available
 * quantity from the ledger. Same shape as PRD.191 batch helpers but
 * a single SQL join so the picker only needs one query.
 *
 * Pharmacy must NOT create new batches — this query is the entire
 * universe of selectable batches at dispense time.
 */
export interface PharmacyBatchOptionRow {
  inventory_batch_id: string;
  batch_number: string | null;
  expiry_date: string | null;
  available_quantity: number | null;
}

export function qAvailableBatchesForDispense(itemId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-available-batches', itemId] as const,
    sql: `
      SELECT
        b.id                                                           AS inventory_batch_id,
        b.batch_number                                                  AS batch_number,
        b.expiry_date                                                   AS expiry_date,
        COALESCE((
          SELECT
            COALESCE(SUM(COALESCE(sm.quantity_in, 0)), 0)
              - COALESCE(SUM(COALESCE(sm.quantity_out, 0)), 0)
          FROM stock_movements sm
          WHERE sm.inventory_batch_id = b.id
        ), 0)                                                            AS available_quantity
      FROM inventory_batches b
      WHERE b.inventory_item_id = ?
      ORDER BY
        CASE WHEN b.expiry_date IS NULL THEN 1 ELSE 0 END,
        b.expiry_date ASC,
        b.batch_number ASC
    `,
    params: [itemId],
    watch: ['inventory_batches', 'stock_movements'],
  };
}

/**
 * Stock movement rows created by a specific Pharmacy dispense — useful
 * for the dispense detail view to confirm the ledger landed correctly.
 */
export function qPharmacyDispenseStockMovements(dispenseId: string): QuerySpec<[string]> {
  return {
    key: ['stock-movements-by-pharmacy-dispense', dispenseId] as const,
    sql: `
      SELECT * FROM stock_movements
      WHERE source_type = 'pharmacy_dispense' AND source_id = ?
      ORDER BY created_at ASC
    `,
    params: [dispenseId],
    watch: ['stock_movements'],
  };
}

/**
 * PRD.204 — Global Pharmacy Dispense History.
 *
 * Confirmed pharmacy dispense lines across ALL patients/encounters, used
 * by the global Pharmacy page's history tab. Read-only audit view; the
 * stock-out event has already been written by Confirm Dispense, so this
 * query never affects stock movements.
 *
 * `patient_name` falls back to `patients.full_name` when the invoice's
 * captured `patient_name` is empty (same approach as the patient/encounter
 * history queries — keeps printable labels filled in).
 *
 * `encounter_id` is `COALESCE(pd.encounter_id, i.encounter_id)` so older
 * dispenses written before `pharmacy_dispenses.encounter_id` was populated
 * directly still resolve to a navigable encounter via the invoice.
 */
export function qGlobalPharmacyDispenseHistory(): QuerySpec<[]> {
  return {
    key: ['global-pharmacy-dispense-history'] as const,
    sql: `
      SELECT
        pd.id              AS pharmacy_dispense_id,
        pdl.id             AS pharmacy_dispense_line_id,
        pd.dispense_number AS dispense_number,
        pd.dispensed_at    AS dispensed_at,
        pd.dispensed_by    AS dispensed_by,
        pd.patient_id      AS patient_id,
        COALESCE(NULLIF(TRIM(i.patient_name), ''), p.full_name) AS patient_name,
        COALESCE(pd.encounter_id, i.encounter_id) AS encounter_id,
        pd.invoice_id      AS invoice_id,
        i.invoice_no       AS invoice_no,
        pdl.invoice_item_id        AS invoice_item_id,
        pdl.inventory_item_id      AS inventory_item_id,
        pdl.item_name_snapshot     AS item_name_snapshot,
        pdl.sku_snapshot           AS sku_snapshot,
        pdl.batch_number_snapshot  AS batch_number_snapshot,
        pdl.expiry_date_snapshot   AS expiry_date_snapshot,
        pdl.unit_snapshot          AS unit_snapshot,
        pdl.instructions_snapshot  AS instructions_snapshot,
        pdl.quantity_dispensed     AS quantity_dispensed,
        pdl.remarks                AS remarks
      FROM pharmacy_dispense_lines pdl
      JOIN pharmacy_dispenses pd ON pd.id = pdl.pharmacy_dispense_id
      LEFT JOIN invoices i ON i.id = pd.invoice_id
      LEFT JOIN patients p ON p.id = pd.patient_id
      WHERE pdl.status = 'confirmed'
      ORDER BY pd.dispensed_at DESC, pdl.created_at ASC
    `,
    params: [],
    watch: ['pharmacy_dispenses', 'pharmacy_dispense_lines', 'invoices'],
  };
}
